import { Component, OnDestroy, OnInit, ViewChild, Inject } from '@angular/core';
import { NgForm } from '@angular/forms';
import { MatDialogRef, MAT_DIALOG_DATA } from '@angular/material/dialog';
import { debounceTime, takeUntil, first } from 'rxjs/operators';
import { Subject, Subscription } from 'rxjs';
import {
  EntityCacheProvider,
  NotificationService,
  DevicesService,
  DialogService,
  UserAuth,
  CommonRegEx,
  IPsService
} from '@nrwl/common-lib';
import { JmepConfigureService } from '../../../../service/jmep-configure.service';
import { isEqual } from 'lodash';
@Component({
  selector: 'jmep-configure',
  templateUrl: './jmep-configure-management.component.html',
  styleUrls: ['./jmep-configure-management.component.scss']
})
export class JmepConfigureManagementComponent implements OnInit, OnDestroy {
  @ViewChild('jmepForm', { static: true }) form: NgForm;
  badIpValues = ['', '0.0.0.0', '255.255.255.255', '0', '::/0'];
  interfaceDisabled = false;
  hasReadWriteRole = false;
  addressIsEmpty = false;
  originalEnablePingResponse = false;
  fieldsMapping: any;
  formDataCopy: object;
  configData: object;
  ipAddressType = '';
  IP_V4 = 'ipv4';
  IP_V6 = 'ipv6';
  isGatewayIpValid = true;
  isGatewayIpValidFromInventory = false;
  deviceList = null;
  gatewayEntity = {
    error: ''
  };
  subnetLengthEntity = {
    placeholder: '1 - 32',
    error: 'Must be 1 - 32'
  };
  loopbackInterface = [
    { value: 'disabled', name: 'Disabled' },
    { value: 'port', name: 'Port' },
    { value: 'source', name: 'Source specific' }
  ];
  ARPOptions = [
    { value: 'disabled', name: 'Disabled' },
    { value: 'enableNetworkInterface', name: 'Enable Network Interface' },
    {
      value: 'enableEquipmentInterface',
      name: 'Enable Equipment Interface'
    },
    { value: 'enableBothInterface', name: 'Enable Both Interfaces' }
  ];
  dyingGaspOptions = [
    { value: 'disabled', name: 'Disabled' },
    { value: 'enableNetworkInterface', name: 'Enable Network Interface' },
    { value: 'enableEquipmentInterface', name: 'Enable Equipment Interface' }
  ];
  timingMode = [
    { value: 'asynchronous', name: 'Asynchronous' },
    { value: 'synchronous', name: 'Synchronous' }
  ];
  formData = {
    name: '',
    location: '',
    lat: '',
    lon: '',
    address: '',
    gateway: '',
    subnet: '',
    vlan: '',
    selectedModeOption: this.timingMode[0],
    selectedARPOption: this.ARPOptions[0],
    selectedDyingGaspOption: this.dyingGaspOptions[0],
    equipmentInterface: this.loopbackInterface[0],
    networkInterface: this.loopbackInterface[0],
    enableViaviHello: false,
    enablePingResponse: false,
    enableLosPin: false
  };
  isLoading = false;
  managementFormDataUpdated = {};
  managementFormData = {};
  GATEWAY = 'gateway';
  SUBNET = 'subnet';
  IP_ADDRESS = 'address';
  TEST_IP_ADDRESS = 'testIpAddress';
  loadVersion2Config = false;
  requestJSONFormat = {
    'config-attributes': {
      name: 'probe-attributes',
      option: [
        {
          name: this.data?.device?.dataItem.nameId,
          id: this.data?.device?.dataItem.id,
          value: {
            data: []
          }
        }
      ]
    }
  };
  finalUpdatedFieldsOptions = [];
  savingInProgress = false;
  managementFieldMapping = {};
  fieldValue: string;
  vlanDisabled = 'false';
  changedFields: object;
  subnetMin = 1;
  subnetMax = 32;
  // eslint-disable-next-line
  additionalFieldNames: any;
  // eslint-disable-next-line
  JMEP_NAME_REGEX: RegExp = /^[a-zA-Z0-9~@#+$%\-:._]+$/;
  latitudeIPRegex: RegExp = CommonRegEx.LATITUDE;
  longitudeIPRegex: RegExp = CommonRegEx.LONGITUDE;
  disableAction = false;
  private readonly unsubscribe: Subject<void> = new Subject();
  private formControl = new Subscription();
  constructor(
    public dialogRef: MatDialogRef<JmepConfigureManagementComponent>,
    readonly entityCache: EntityCacheProvider,
    private notificationService: NotificationService,
    private jmepConfigureService: JmepConfigureService,
    private readonly deviceService: DevicesService,
    private readonly dialogService: DialogService,
    private readonly userAuth: UserAuth,
    private readonly ipsService: IPsService,
    @Inject(MAT_DIALOG_DATA) public data: any // eslint-disable-line
  ) {
    this.hasReadWriteRole = this.userAuth.isAuthedFor('READ_WRITE');
  }

  ngOnInit() {
    this.isLoading = true;
    this.fieldsMapping = this.getFieldsAttributesMapping();
    const reqData = {
      name: this.data?.device?.dataItem.nameId,
      id: this.data?.device?.dataItem.id
    };
    this.jmepConfigureService
      .getJMEPConfig(reqData)
      .pipe(takeUntil(this.unsubscribe))
      .subscribe(
        res => {
          this.configData = res.data;
          this.isLoading = false;
          this.initConfigDetails();
        },

        () => {
          this.isLoading = false;
        }
      );
    this.formControl = this.form.control.valueChanges.pipe(debounceTime(200)).subscribe(() => {
      this.disableAction = this.isFormValueChanged();
    });
    this.getDevicesList();
  }
  //eslint-disable-next-line
  getTestIpAddressType(): string {
    let type = '';
    if (this.formData.address && this.ipsService.isValidIP(this.formData.address)) {
      const isIpv4 = this.ipsService.isIPv4(this.formData.address);
      type = isIpv4 ? this.IP_V4 : this.IP_V6;
    }
    return type;
  }

  validateIpAddress(): void {
    if (this.badIpValues.includes(this.formData.address)) {
      this.addressIsEmpty = true;
      this.formData.enablePingResponse = false;
      this.formData.selectedARPOption = this.ARPOptions[0];
      this.formData.gateway = '';
      this.formData.subnet = '';
    } else {
      this.addressIsEmpty = false;
      this.formData.enablePingResponse = this.originalEnablePingResponse;
      const isvalidIP = this.ipsService.isValidIP(this.formData.address);
      if (isvalidIP) {
        this.ipAddressType = this.getTestIpAddressType();
        if (this.ipAddressType === this.IP_V4) {
          this.subnetLengthEntity.placeholder = '1 - 32';
        } else {
          this.subnetLengthEntity.placeholder = '1 - 128';
        }
      } else {
        this.form.controls?.address.setErrors({ invalid: true });
      }
      this.validateGatewayChange();
      this.validateSubnetChange();
    }
  }

  validateSubnetChange(): void {
    if (this.formData.subnet) {
      let retval = true;
      if (this.ipAddressType === this.IP_V4) {
        retval = parseInt(this.formData.subnet) >= 1 && parseInt(this.formData.subnet) <= 32;
        this.subnetLengthEntity.error = 'Must be 1 - 32';
      }
      if (this.ipAddressType === this.IP_V6) {
        retval = parseInt(this.formData.subnet) >= 1 && parseInt(this.formData.subnet) <= 128;
        this.subnetLengthEntity.error = 'Must be 1 - 128';
      }
      if (!retval) {
        this.form.controls?.subnet.setErrors({ invalid: 'true' });
        this.form.controls?.subnet.markAsDirty();
      }
    } else {
      this.subnetLengthEntity['error'] = 'required';
      this.form.controls?.subnet.setErrors({ invalid: 'true' });
      this.form.controls?.subnet.markAsDirty();
    }
  }

  validateGatewayChange(): void {
    this.isGatewayIpValid = true;
    if (this.badIpValues.includes(this.formData.gateway)) {
      this.gatewayEntity.error = 'required';
      this.form.controls?.gateway.setErrors({ invalid: 'true' });
      this.form.controls?.gateway.markAsDirty();
      this.isGatewayIpValid = false;
    } else {
      if (this.ipAddressType === this.IP_V4) {
        const isValidIp = this.ipsService.isIPv4(this.formData.gateway);
        if (!isValidIp) {
          this.gatewayEntity.error = 'Not a valid IPv4 address';
          this.form.controls?.gateway.setErrors({ invalid: 'true' });
          this.form.controls?.gateway.markAsDirty();
        }
      } else {
        if (this.ipAddressType === this.IP_V6) {
          const isValidIp = this.ipsService.isIPv6(this.formData.gateway);
          if (!isValidIp) {
            this.gatewayEntity.error = 'Not a valid IPv6 address';
            this.form.controls?.gateway.setErrors({ invalid: 'true' });
            this.form.controls?.gateway.markAsDirty();
          }
        }
      }
    }
  }

  ngOnDestroy() {
    this.unsubscribe.next();
    this.unsubscribe.complete();
    this.formControl.unsubscribe();
  }
  // eslint-disable-next-line
  getFieldsAttributesMapping(): any {
    return {
      name: {
        section: 'description',
        attributes: ['display_name'],
        defaultValue: ''
      },
      location: {
        section: 'description',
        attributes: ['location'],
        defaultValue: ''
      },
      lat: {
        section: 'location',
        attributes: ['lat'],
        defaultValue: ''
      },
      lon: {
        section: 'location',
        attributes: ['lon'],
        defaultValue: '',
        additionalFieldsMapping: {
          attributes: [],
          dataValue: []
        }
      },
      address: {
        section: 'network',
        attributes: ['ip'],
        defaultValue: ''
      },
      gateway: {
        section: 'network',
        attributes: ['gateway_ip'],
        defaultValue: ''
      },
      subnet: {
        section: 'network',
        attributes: ['netmask_length'],
        defaultValue: ''
      },
      vlan: {
        section: 'network',
        attributes: ['mgmt_vlan'],
        defaultValue: '',
        additionalFieldsMapping: {
          attributes: ['mgmt_vlan_socp_pass_thru'],
          dataValue: ['false']
        }
      },
      selectedARPOption: {
        section: 'network',
        attributes: ['arp_adp_enabled_optical', 'arp_adp_enabled_host'],
        dataMapping: [
          {
            dataValue: ['false', 'false'],
            formValue: this.ARPOptions[0]
          },
          {
            dataValue: ['true', 'false'],
            formValue: this.ARPOptions[1]
          },
          {
            dataValue: ['false', 'true'],
            formValue: this.ARPOptions[2]
          },
          {
            dataValue: ['true', 'true'],
            formValue: this.ARPOptions[3]
          }
        ],
        defaultValue: this.ARPOptions[0]
      },
      selectedDyingGaspOption: {
        section: 'otherOptions',
        attributes: [
          'dying_gasp_frp_enabled',
          'dying_gasp_optical_link_oam_enabled',
          'dying_gasp_host_link_oam_enabled'
        ],
        dataMapping: [
          {
            dataValue: ['false', 'false', 'false'],
            formValue: this.dyingGaspOptions[0]
          },
          {
            dataValue: ['true', 'true', 'false'],
            formValue: this.dyingGaspOptions[1]
          },
          {
            dataValue: ['true', 'false', 'true'],
            formValue: this.dyingGaspOptions[2]
          }
        ],
        defaultValue: this.dyingGaspOptions[0]
      },
      selectedModeOption: {
        section: 'timing',
        attributes: ['mode'],
        dataMapping: [
          {
            dataValue: ['asynch_timing'],
            formValue: this.timingMode[0]
          },
          {
            dataValue: ['through_timing'],
            formValue: this.timingMode[1]
          }
        ],
        defaultValue: this.timingMode[0]
      },
      enablePingResponse: {
        section: 'otherOptions',
        attributes: ['ping_ems_ip_pingable'],
        dataMapping: [
          {
            dataValue: ['false'],
            formValue: false
          },
          {
            dataValue: ['true'],
            formValue: true
          }
        ],
        additionalFieldsMapping: {
          attributes: ['ping_customer_visible_ip_pingable'],
          dataValue: ['false']
        },
        defaultValue: false
      },
      enableLosPin: {
        section: 'los',
        attributes: ['mask_los_pin'],
        dataMapping: [
          {
            dataValue: ['false'],
            formValue: false
          },
          {
            dataValue: ['true'],
            formValue: true
          }
        ],
        defaultValue: false
      },
      equipmentInterface: {
        section: 'loopback',
        attributes: [
          'accept_layer2_port_loopback_request_host',
          'accept_layer3_port_loopback_request_host',
          'accept_layer2_evc_loopback_request_host',
          'accept_layer3_evc_loopback_request_host'
        ],
        dataMapping: [
          {
            dataValue: ['false', 'false', 'false', 'false'],
            formValue: this.loopbackInterface[0]
          },
          {
            dataValue: ['true', 'true', 'false', 'false'],
            formValue: this.loopbackInterface[1]
          },
          {
            dataValue: ['false', 'false', 'true', 'true'],
            formValue: this.loopbackInterface[2]
          }
        ],
        defaultValue: this.loopbackInterface[0]
      },
      networkInterface: {
        section: 'loopback',
        attributes: [
          'accept_layer2_port_loopback_request_optical',
          'accept_layer3_port_loopback_request_optical',
          'accept_layer2_evc_loopback_request_optical',
          'accept_layer3_evc_loopback_request_optical'
        ],
        dataMapping: [
          {
            dataValue: ['false', 'false', 'false', 'false'],
            formValue: this.loopbackInterface[0]
          },
          {
            dataValue: ['true', 'true', 'false', 'false'],
            formValue: this.loopbackInterface[1]
          },
          {
            dataValue: ['false', 'false', 'true', 'true'],
            formValue: this.loopbackInterface[2]
          }
        ],
        defaultValue: this.loopbackInterface[0]
      },
      enableViaviHello: {
        section: 'loopback',
        attributes: ['respond_to_hello_request_optical', 'respond_to_hello_request_host'],
        dataMapping: [
          {
            dataValue: ['false', 'false'],
            formValue: false
          },
          {
            dataValue: ['true', 'true'],
            formValue: true
          }
        ],
        defaultValue: false
      }
    };
  }
  getTextFormValue(fieldName: string): string {
    const formValue = this.jmepConfigureService.getProbeAttibuteValues(
      this.configData,
      this.fieldsMapping[fieldName].section,
      this.fieldsMapping[fieldName].attributes,
      this.fieldsMapping[fieldName].defaultValue
    );
    return formValue.join();
  }
  onInterfaceChange(): void {
    this.interfaceDisabled =
      this.formData.equipmentInterface.value === 'disabled' &&
      this.formData.networkInterface.value === 'disabled';
  }
  // eslint-disable-next-line
  getVlanValue(): any {
    const vlanValue = this.getTextFormValue('vlan');
    const vlanDisabledState = this.jmepConfigureService
      .getProbeAttibuteValues(
        this.configData,
        this.fieldsMapping.vlan.section,
        ['mgmt_vlan_disabled'],
        'false'
      )
      .join();
    return vlanValue === '0' || vlanDisabledState === 'true'
      ? this.fieldsMapping.vlan.defaultValue
      : vlanValue;
  }
  // eslint-disable-next-line
  initConfigDetails(): any {
    this.formData.name = this.getTextFormValue('name');
    this.formData.location = this.getTextFormValue('location');
    this.formData.lat = this.getTextFormValue('lat');
    this.formData.lon = this.getTextFormValue('lon');
    this.formData.address = this.getTextFormValue('address');

    this.formData.gateway = this.getTextFormValue('gateway');
    this.formData.subnet = this.getTextFormValue('subnet');

    this.formData.vlan = this.getVlanValue();

    this.formData.selectedARPOption = this.jmepConfigureService.getFormValueByAttributes(
      this.configData,
      this.fieldsMapping.selectedARPOption
    );

    this.formData.selectedDyingGaspOption = this.jmepConfigureService.getFormValueByAttributes(
      this.configData,
      this.fieldsMapping.selectedDyingGaspOption
    );

    this.formData.selectedModeOption = this.jmepConfigureService.getFormValueByAttributes(
      this.configData,
      this.fieldsMapping.selectedModeOption
    );

    this.formData.enablePingResponse = this.jmepConfigureService.getFormValueByAttributes(
      this.configData,
      this.fieldsMapping.enablePingResponse
    );

    this.formData.enableLosPin = this.jmepConfigureService.getFormValueByAttributes(
      this.configData,
      this.fieldsMapping.enableLosPin
    );

    this.formData.enableViaviHello = this.jmepConfigureService.getFormValueByAttributes(
      this.configData,
      this.fieldsMapping.enableViaviHello
    );

    this.formData.equipmentInterface = this.jmepConfigureService.getFormValueByAttributes(
      this.configData,
      this.fieldsMapping.equipmentInterface
    );

    this.formData.networkInterface = this.jmepConfigureService.getFormValueByAttributes(
      this.configData,
      this.fieldsMapping.networkInterface
    );

    if (this.formData.address === '0.0.0.0' || this.formData.address === '255.255.255.255') {
      this.formData.address = '';
      this.validateAddress();
    }
    if (
      '' === this.formData.gateway ||
      this.formData.gateway === '0.0.0.0' ||
      this.formData.gateway === '255.255.255.255'
    ) {
      this.formData.gateway = '';
    } else {
      this.isGatewayIpValidFromInventory = true;
    }
    this.ipAddressType = this.getTestIpAddressType();
    this.formDataCopy = Object.assign({}, this.formData);
    this.onInterfaceChange();
  }
  //eslint-disable-next-line
  validateAddress() {
    if (
      this.formData.address === '' ||
      this.formData.address === '0.0.0.0' ||
      this.formData.address === '255.255.255.255'
    ) {
      this.addressIsEmpty = true;
      this.formData.enablePingResponse = false;
      this.formData.selectedARPOption = this.ARPOptions[0];
      this.formData.gateway = '';
      this.formData.subnet = '';
    } else if (!this.addressIsEmpty) {
      this.addressIsEmpty = false;
      this.formData.enablePingResponse = this.originalEnablePingResponse;
    }
  }
  //eslint-disable-next-line
  onClickSelectedValues(value, type) {
    switch (type) {
      case 'arp':
        this.formData.selectedARPOption = this.ARPOptions.find(item => item.value === value);
        break;
      case 'equipmentInterface':
        this.formData.equipmentInterface = this.loopbackInterface.find(
          item => item.value === value
        );
        break;
      case 'networkInterface':
        this.formData.networkInterface = this.loopbackInterface.find(item => item.value === value);
        break;
      case 'selectedDyingGaspOption':
        this.formData.selectedDyingGaspOption = this.dyingGaspOptions.find(
          item => item.value === value
        );
        break;
      case 'selectedModeOption':
        this.formData.selectedModeOption = this.timingMode.find(item => item.value === value);
        break;
    }
  }
  //eslint-disable-next-line
  saveConfigDetails(): any {
    const managementChangedFields = this.filterChangedFields(this.formDataCopy, this.formData);
    if (managementChangedFields && Object.keys(managementChangedFields).length) {
      this.updateConfigManagement(managementChangedFields);
    }
  } //eslint-disable-next-line
  filterChangedFields(oldFormValue: object, newFormValue: object): any {
    const updatedFields = {};
    const IpAttribute = this.loadVersion2Config ? this.TEST_IP_ADDRESS : this.IP_ADDRESS;
    for (const key of Object.keys(oldFormValue)) {
      if (!isEqual(oldFormValue[key], newFormValue[key])) {
        updatedFields[key] = newFormValue[key];

        if (key === 'address') {
          updatedFields['selectedARPOption'] = newFormValue['selectedARPOption'];
        }
        if (key === 'subnet' || key === 'gateway') {
          updatedFields['subnet'] = newFormValue['subnet'];
          updatedFields['gateway'] = newFormValue['gateway'];
        }
        if (this.loadVersion2Config) {
          if (key === 'enablePingResponse') {
            updatedFields['enablePingResponse'] = newFormValue['enablePingResponse'];
          }
        }

        //Updating location
        if (key === 'lat' || key === 'lon' || key === 'location') {
          updatedFields['lat'] = newFormValue['lat'];
          updatedFields['lon'] = newFormValue['lon'];
          updatedFields['location'] = newFormValue['location'];
        }
      }
    }
    if ('' === newFormValue[IpAttribute] || '0.0.0.0' == newFormValue[IpAttribute]) {
      delete updatedFields[this.GATEWAY];
      delete updatedFields[this.SUBNET];
    }

    return updatedFields;
  }
  //eslint-disable-next-line
  callBackSaveAck(service): void {
    service.callBackJmepConfigSave();
  }
  //eslint-disable-next-line
  callBackJmepConfigSave(): any {
    this.saveConfigStart(this.changedFields);
  }
  updateConfigManagement(managementChangedFields: object): void {
    this.changedFields = managementChangedFields;
    //eslint-disable-next-line
    if (
      managementChangedFields &&
      (Object.prototype.hasOwnProperty.call(managementChangedFields, 'address') ||
        Object.prototype.hasOwnProperty.call(managementChangedFields, 'enableLosPin') ||
        Object.prototype.hasOwnProperty.call(managementChangedFields, 'selectedARPOption') ||
        Object.prototype.hasOwnProperty.call(managementChangedFields, 'selectedModeOption'))
    ) {
      if (
        Object.prototype.hasOwnProperty.call(managementChangedFields, 'selectedARPOption') &&
        (managementChangedFields['selectedARPOption'].value === 'enableEquipmentInterface' ||
          managementChangedFields['selectedARPOption'].value === 'enableNetworkInterface')
      ) {
        const interfaceName =
          managementChangedFields['selectedARPOption'].value === 'enableEquipmentInterface'
            ? 'equipment'
            : 'network';
        const param = {
          id: 'datasource.jmepConfig',
          alternateMsg:
            `ARP/NDP for the management network is about to be disabled on the ` +
            interfaceName +
            `equipment interface.
          This could result in loss of communication with the JMEP.Are you sure you want to apply these changes?`,
          callBackService: this,
          callBackFuntion: this.callBackSaveAck
        };
        this.dialogService.showDialog('confirm', 'body', param);
      } else {
        const param = {
          id: 'datasource.jmepConfig',
          alternateMsg: `The settings you have changed may cause the system to lose contact with the JMEP. Are you sure you want to apply these changes?`,
          callBackService: this,
          callBackFuntion: this.callBackSaveAck
        };
        this.dialogService.showDialog('confirm', 'body', param);
      }
    } else {
      this.saveConfigStart(managementChangedFields);
    }
  }
  //eslint-disable-next-line
  saveConfigStart(updatedFields: any): void {
    this.finalUpdatedFieldsOptions = [];
    this.savingInProgress = true;
    if (updatedFields.address === '' || updatedFields.address === '255.255.255.255') {
      updatedFields.address = '0.0.0.0';
    }
    this.getUpdatedFieldAttributes(updatedFields);
    this.requestJSONFormat['config-attributes'].option[0].value.data =
      this.finalUpdatedFieldsOptions;
    this.updateJMEPConfig(this.requestJSONFormat);
  }
  //eslint-disable-next-line
  updateJMEPConfig(requestJSONFormat): any {
    this.isLoading = true;
    //eslint-disable-next-line
    this.jmepConfigureService
      .setJMEPConfig(requestJSONFormat)
      .pipe(takeUntil(this.unsubscribe))
      .subscribe(
        res => {
          this.configData = res.data;
          this.isLoading = false;
          this.deviceService.updateCache();
          this.notificationService.onSuccess(
            'Configuration has been applied to JMEP : ' + this.formData.name
          );
          this.dialogRef.close();
        },
        error => {
          this.isLoading = false;
          this.notificationService.onFail(
            'Failed to apply configuration to JMEP : ' +
              this.jmepConfigureService.parseErrorMessage(error?.error?.data?.message)
          );
          this.dialogRef.close();
        }
      );
  }
  //eslint-disable-next-line
  getUpdatedFieldAttributes(updatedFields) {
    for (const key of Object.keys(updatedFields)) {
      const fieldAttributes = this.fieldsMapping[key];
      switch (key) {
        case 'name':
        case 'location':
        case 'address':
        case 'gateway':
        case 'subnet':
          this.createProbeAttributes(
            fieldAttributes.section,
            fieldAttributes.attributes,
            [updatedFields[key]],
            fieldAttributes.additionalFieldsMapping
          );
          break;
        case 'lat':
        case 'lon':
          this.additionalFieldNames = fieldAttributes.additionalFieldsMapping;
          if (this.additionalFieldNames) {
            this.additionalFieldNames.attributes.push('address');
            this.additionalFieldNames.dataValue.push(this.formData.location);
          }
          this.createProbeAttributes(
            fieldAttributes.section,
            fieldAttributes.attributes,
            [updatedFields[key]],
            this.additionalFieldNames
          );
          break;
        case 'vlan':
          this.fieldValue = updatedFields[key] ? updatedFields[key] : '0';
          this.vlanDisabled = updatedFields[key] ? 'false' : 'true';
          this.additionalFieldNames = fieldAttributes.additionalFieldsMapping;
          this.additionalFieldNames.attributes.push('mgmt_vlan_disabled');
          this.additionalFieldNames.dataValue.push(this.vlanDisabled);
          this.createProbeAttributes(
            fieldAttributes.section,
            fieldAttributes.attributes,
            [this.fieldValue],
            this.additionalFieldNames
          );
          break;
        case 'testVlan':
          this.fieldValue = updatedFields[key] ? updatedFields[key] : '0';
          this.vlanDisabled = updatedFields[key] ? 'true' : 'false';
          this.additionalFieldNames = fieldAttributes.additionalFieldsMapping;
          this.additionalFieldNames.attributes.push('test_ip_vlan_enabled');
          this.additionalFieldNames.dataValue.push(this.vlanDisabled);
          this.createProbeAttributes(
            fieldAttributes.section,
            fieldAttributes.attributes,
            [this.fieldValue],
            this.additionalFieldNames
          );
          break;
        case 'selectedModeOption':
        case 'selectedARPOption':
        case 'selectedDyingGaspOption':
        case 'enablePingResponse':
        case 'enableLosPin':
        case 'equipmentInterface':
        case 'networkInterface':
        case 'enableViaviHello':
          //eslint-disable-next-line
          const fieldValues = fieldAttributes.dataMapping.find(
            data => JSON.stringify(data.formValue) === JSON.stringify(updatedFields[key])
          );
          this.createProbeAttributes(
            fieldAttributes.section,
            fieldAttributes.attributes,
            fieldValues.dataValue,
            fieldAttributes.additionalFieldsMapping
          );
          break;
      }
    }
  }
  //eslint-disable-next-line
  createProbeAttributes(section, fieldNames, fieldValues, additionalFieldNames): void {
    const subAttributesArray = [];
    fieldNames.forEach((fieldName, index) => {
      subAttributesArray.push({
        id: fieldName,
        value: fieldValues[index]
      });
    });
    if (additionalFieldNames) {
      additionalFieldNames.attributes.forEach((fieldName, index) => {
        subAttributesArray.push({
          id: fieldName,
          value: additionalFieldNames.dataValue[index]
        });
      });
    }
    const sectionIndex = this.finalUpdatedFieldsOptions.findIndex(field => field.name === section);
    if (sectionIndex >= 0) {
      this.finalUpdatedFieldsOptions[sectionIndex].subAttributes =
        this.finalUpdatedFieldsOptions[sectionIndex].subAttributes.concat(subAttributesArray);
    } else {
      this.finalUpdatedFieldsOptions.push({ name: section, subAttributes: subAttributesArray });
    }
  }

  isFormValueChanged(): boolean {
    return JSON.stringify(this.formDataCopy) === JSON.stringify(this.formData);
  }

  validateNameChange(): void {
    let agents = {};
    if (this.formData.name) {
      if (this.JMEP_NAME_REGEX.test(this.formData.name)) {
        if (this.formData.name && this.deviceList.length) {
          if (this.formDataCopy?.['name'] !== this.formData.name) {
            agents = this.deviceList.find(item => item?.deviceData?.nameId === this.formData.name);
          }
        }
        if (agents && Object.keys(agents)?.length > 0) {
          this.form.controls?.jmepName.setErrors({ uniqueName: 'false' });
          this.form.controls?.jmepName.markAsDirty();
        }
      } else {
        this.form.controls?.jmepName.setErrors({ pattern: 'false' });
        this.form.controls?.jmepName.markAsDirty();
      }
    }
  }

  getDevicesList(): void {
    this.entityCache
      .findEntityCache('device')
      .getValuesAsync()
      .pipe(first())
      .subscribe(device => {
        this.deviceList = device;
      });
  }
}
